import {
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Textarea,
  Button,
  IconButton,
  Divider
} from '@mui/joy';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useFeedback } from './feedback/Service';
import DefaultInterviewQuestionsSelector from './DefaultInterviewQuestionsSelector';

export const hasInvalidInterviewQuestions = (sections = []) =>
  sections.some(section => (section.questions || []).some(q => {
    if (typeof q === 'string') return !q.trim();
    if (typeof q === 'object' && q !== null) {
      return !q.title?.trim() ||
        !q.candidateRole?.trim() ||
        !q.buyerPersona?.trim() ||
        !q.context?.trim() ||
        !q.buyerBackground?.trim() ||
        !q.candidateObjective?.trim();
    }
    return false;
  }));

const InterviewQuestionsEditor = ({ questions: sections = [], setQuestions: setSections, onClose, embedded = false }) => {
  // Initialize state with default questions or provided ones
  // const [sections, setSections] = useState([]);
  const feedback = useFeedback();

  const handleSave = () => {
    // Check required fields
    if (hasInvalidInterviewQuestions(sections)) {
      feedback.snackbar({ text: "All fields are required. Please fill in all fields.", type: "error" });
      return;
    }
    onClose?.();
  }

  // Function to update a question
  const handleQuestionChange = (sectionIndex, questionIndex, field, newValue) => {
    const newSections = [...sections];
    const question = newSections[sectionIndex].questions[questionIndex];

    if (typeof question === 'object') {
      newSections[sectionIndex].questions[questionIndex] = {
        ...question,
        [field]: newValue
      };
    } else {
      newSections[sectionIndex].questions[questionIndex] = newValue;
    }
    setSections(newSections);
  };

  // Function to add a new question to a section
  const handleAddQuestion = (sectionIndex) => {
    const newSections = [...sections];
    const section = newSections[sectionIndex];

    // Check if this section should have structured questions (object format)
    // Section 4 (Role Play) uses structured questions, identified by sectionTitle containing "Role Play"
    // or by checking if any existing question is an object
    const isRolePlaySection = section.sectionTitle?.toLowerCase().includes('role play') ||
      section.sectionTitle?.toLowerCase().includes('roleplay');
    const hasStructuredQuestions = section.questions.length > 0 &&
      typeof section.questions[0] === 'object';

    if (isRolePlaySection || hasStructuredQuestions) {
      // Add a structured question object for role-play scenarios
      newSections[sectionIndex].questions.push({
        title: '',
        candidateRole: '',
        buyerPersona: '',
        context: '',
        buyerBackground: '',
        candidateObjective: ''
      });
    } else {
      // Add a simple string question
      newSections[sectionIndex].questions.push('');
    }

    setSections(newSections);
  };

  // Function to remove a question from a section
  const handleRemoveQuestion = (sectionIndex, questionIndex) => {
    const newSections = [...sections];
    newSections[sectionIndex].questions.splice(questionIndex, 1);
    setSections(newSections);
  };

  return (
    <Box sx={{ p: embedded ? 0 : 2, mx: 'auto', width: '100%', pt: 0 }} maxWidth={embedded ? undefined : "md"}>
      {!embedded && (
        <Stack direction="row" alignItems="center" mb={0} sx={{ position: "sticky", top: 0, py: 2, backgroundColor: 'background.body', zIndex: 10, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography level="h4" mb={0}>Interview Questions</Typography>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            sx={{ mt: 0, float: 'right', ml: 'auto', mr: 0 }}
          >
            Save & Close
          </Button>
        </Stack>
      )}
      
      <Box sx={{ mb: 3, mt: 2 }}>
        <DefaultInterviewQuestionsSelector
          interviewQuestions={sections}
          setInterviewQuestions={setSections}
          showHelperText={false}
        />
      </Box>
      
      <Typography level="body-md" mb={3} mt={0}>
        AI will use these as base questions. It may slightly rephrase them and ask smart follow-ups, tailored to the candidate&apos;s resume and answers.
      </Typography>
      <Stack spacing={4}>
        {sections.map((section, sectionIndex) => (
          <Card key={sectionIndex} variant="outlined">
            <CardContent>
              <Typography level="h5" fontWeight="bold" sx={{ mb: 0 }}>Section {sectionIndex + 1}: {section.sectionTitle}</Typography>

              <Divider sx={{ my: 2 }} />

              {/* <Typography level="body-md" fontWeight="bold" sx={{ mb: 2 }}>Questions:</Typography> */}

              <Stack spacing={2}>
                {section.questions.map((question, questionIndex) => {
                  const isObjectQuestion = typeof question === 'object';

                  return (
                    <Stack
                      key={questionIndex}
                      spacing={1}
                      sx={{
                        p: isObjectQuestion ? 2 : 0,
                        border: isObjectQuestion ? '1px solid' : 'none',
                        borderColor: 'divider',
                        borderRadius: 'sm'
                      }}
                    >
                      {isObjectQuestion ? (
                        <>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Stack spacing={1} sx={{ flexGrow: 1 }}>
                              <Typography level="body-sm" fontWeight="bold">Title</Typography>
                              <Textarea
                                value={question.title || ''}
                                onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'title', e.target.value)}
                                minRows={1}
                                placeholder="e.g., Cold Call to Logistics VP"
                                required
                              />
                            </Stack>
                            <IconButton
                              variant="plain"
                              color="danger"
                              onClick={() => handleRemoveQuestion(sectionIndex, questionIndex)}
                              title="Remove question"
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>

                          <Box>
                            <Typography level="body-sm" fontWeight="bold">Candidate Role</Typography>
                            <Textarea
                              value={question.candidateRole || ''}
                              onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'candidateRole', e.target.value)}
                              minRows={1}
                              placeholder="e.g., SDR at AcmeTech"
                              required
                            />
                          </Box>

                          <Box>
                            <Typography level="body-sm" fontWeight="bold">Buyer Persona</Typography>
                            <Textarea
                              value={question.buyerPersona || ''}
                              onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'buyerPersona', e.target.value)}
                              minRows={1}
                              placeholder="e.g., VP of Operations at FastTrack Logistics"
                              required
                            />
                          </Box>

                          <Box>
                            <Typography level="body-sm" fontWeight="bold">Context</Typography>
                            <Textarea
                              value={question.context || ''}
                              onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'context', e.target.value)}
                              minRows={2}
                              placeholder="Describe the scenario context"
                              required
                            />
                          </Box>

                          <Box>
                            <Typography level="body-sm" fontWeight="bold">Buyer Background</Typography>
                            <Textarea
                              value={question.buyerBackground || ''}
                              onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'buyerBackground', e.target.value)}
                              minRows={2}
                              placeholder="Describe the buyer's background and situation"
                              required
                            />
                          </Box>

                          <Box>
                            <Typography level="body-sm" fontWeight="bold">Candidate Objective</Typography>
                            <Textarea
                              value={question.candidateObjective || ''}
                              onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'candidateObjective', e.target.value)}
                              minRows={2}
                              placeholder="What should the candidate achieve?"
                              required
                            />
                          </Box>
                        </>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Textarea
                            value={question}
                            onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, null, e.target.value)}
                            minRows={2}
                            placeholder="Enter question here"
                            sx={{ flexGrow: 1 }}
                            required
                          />
                          <IconButton
                            variant="plain"
                            color="danger"
                            onClick={() => handleRemoveQuestion(sectionIndex, questionIndex)}
                            title="Remove question"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  );
                })}

                {section.questions.length < section.maxQuestions &&
                  <Button
                    startDecorator={<AddIcon />}
                    variant="soft"
                    color="primary"
                    onClick={() => handleAddQuestion(sectionIndex)}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  >
                    Add Question
                  </Button>
                }
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default InterviewQuestionsEditor;
